import { useState, useRef, forwardRef, useImperativeHandle, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { IoLockClosed } from 'react-icons/io5';
import * as Sentry from '@sentry/react';
import { trackEvent, EVENTS } from '../utils/statsig';
import { reportError } from '../Api/QuoteApi';
import { isUsableServiceAddress } from '../utils/address';
import { CC_FEE_RATE } from '../_lib/agreement-clauses';
import { detectBlockingBrowser } from '../utils/detectBlockingBrowser';
import PaymentBlockedError from './PaymentBlockedError';

const QBO_TOKEN_URL = process.env.REACT_APP_QBO_SANDBOX === 'true'
  ? 'https://sandbox.api.intuit.com/quickbooks/v4/payments/tokens'
  : 'https://api.intuit.com/quickbooks/v4/payments/tokens';

// US ZIP -> state derivation. Phone-lead intake sometimes captures a
// misspelled state name ("Caifornia") or no state at all, so when the
// service address has a missing/unrecognized state we fall back to
// inferring it from the zip prefix. This map covers all 50 states + DC
// and is the standard USPS prefix-to-state assignment. Returns "" when
// the zip can't be matched (e.g. <3 digits, foreign zip, PO box prefix
// not in the table) so callers can still surface an editable field.
const ZIP_TO_STATE_RANGES = [
  [5,5,'NY'],[10,27,'MA'],[28,29,'RI'],[30,38,'NH'],[39,49,'ME'],
  [50,59,'VT'],[60,69,'CT'],[70,89,'NJ'],[100,149,'NY'],[150,196,'PA'],
  [197,199,'DE'],[200,205,'DC'],[206,219,'MD'],[220,246,'VA'],[247,268,'WV'],
  [270,289,'NC'],[290,299,'SC'],[300,319,'GA'],[320,349,'FL'],[350,369,'AL'],
  [370,385,'TN'],[386,397,'MS'],[400,427,'KY'],[430,459,'OH'],[460,479,'IN'],
  [480,499,'MI'],[500,528,'IA'],[530,549,'WI'],[550,567,'MN'],[570,577,'SD'],
  [580,588,'ND'],[590,599,'MT'],[600,629,'IL'],[630,658,'MO'],[660,679,'KS'],
  [680,693,'NE'],[700,714,'LA'],[716,729,'AR'],[730,749,'OK'],[750,799,'TX'],
  [800,816,'CO'],[820,831,'WY'],[832,838,'ID'],[840,847,'UT'],[850,865,'AZ'],
  [870,884,'NM'],[889,898,'NV'],[900,961,'CA'],[967,968,'HI'],[970,979,'OR'],
  [980,994,'WA'],[995,999,'AK'],
];
function stateFromZip(zip) {
  const z = String(zip || '').replace(/\D/g, '').slice(0, 5);
  if (z.length < 3) return '';
  const p = parseInt(z.slice(0, 3), 10);
  for (const [lo, hi, st] of ZIP_TO_STATE_RANGES) {
    if (p >= lo && p <= hi) return st;
  }
  return '';
}

// crypto.randomUUID exists in Safari 15.4+, Chrome 92+, Firefox 95+.
// Older mobile browsers (notably iOS <14.5) throw if we call it
// directly, which would break the entire submit flow. The Intuit
// `Request-Id` header just needs a unique string per request, so a
// timestamp + random hex is a safe fallback.
function generateRequestId() {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    try { return crypto.randomUUID(); } catch { /* fall through */ }
  }
  return `${Date.now().toString(16)}-${Math.random().toString(16).slice(2, 10)}-${Math.random().toString(16).slice(2, 10)}`;
}

// Connectivity self-probe, run ONLY after a tokenize network failure
// (never on a successful payment, so it adds zero latency to the happy
// path). Fingerprints what the customer's device can actually reach so we
// can tell an Intuit-specific block (ad blocker / VPN / private DNS) apart
// from a general outage. no-cors -> we only learn reachability (an opaque
// resolve = reachable) and never read any cross-origin response body. Each
// probe is capped at 3s so a hung network can't stall the error UX.
async function probeConnectivity() {
  const ping = (url) => Promise.race([
    fetch(url, { mode: 'no-cors', cache: 'no-store' }).then(() => true, () => false),
    new Promise((resolve) => setTimeout(() => resolve(false), 3000)),
  ]);
  const [intuit, origin, control] = await Promise.all([
    ping('https://api.intuit.com/'),
    ping(`${window.location.origin}/favicon.ico?cb=${Date.now()}`),
    ping('https://www.gstatic.com/generate_204'),
  ]);
  return { intuit, origin, control };
}

// QBO Payments tokenization expects a 10-digit US phone for ACH.
// Strip everything that isn't a digit; keep the trailing 10.
const normalizeUsPhone = (raw) => {
  const digits = String(raw || '').replace(/\D/g, '');
  return digits.length >= 10 ? digits.slice(-10) : '';
};

const PaymentForm = forwardRef(({ brandConfig, serviceAddress, customerPhone, customerEmail, isMonthly, monthlyPrice, oneTimeTotal }, ref) => {
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { dealId: routeDealId } = useParams();
  const [method, setMethod] = useState('cc');
  // Card is the default; ACH stays collapsed behind a "Pay with bank
  // account instead" link until the customer asks for it. This reduces
  // the perceived choice-friction on the most common path.
  const [achRevealed, setAchRevealed] = useState(false);
  // canUseServiceAddress is derived from the prefilled serviceAddress.
  // When false (Sell data was incomplete), we hide the "same as service"
  // checkbox entirely and force the customer through the editable
  // billing fields. The pre-2026-05-26 behavior (one-shot auto-uncheck
  // that the customer could reverse) was the proximate cause of the
  // Demers incident: customer re-ticked the box and submitted with an
  // empty billing zip. See `serviceAddressFields` on the publisher side.
  const canUseServiceAddress = (() => {
    if (!serviceAddress) return false;
    const filledByZip = serviceAddress.state || stateFromZip(serviceAddress.zip || '');
    return isUsableServiceAddress({
      street: serviceAddress.street,
      city: serviceAddress.city,
      state: filledByZip,
      zip: serviceAddress.zip,
    });
  })();
  const [sameAsService, setSameAsService] = useState(canUseServiceAddress);
  const [billingAddress, setBillingAddress] = useState({
    street: '',
    city: '',
    state: '',
    zip: '',
  });
  const [ccFields, setCcFields] = useState({
    cardNumber: '',
    expiry: '',
    cvv: '',
    nameOnCard: '',
  });
  const [achFields, setAchFields] = useState({
    routingNumber: '',
    accountNumber: '',
    accountType: 'checking',
    nameOnAccount: '',
  });
  const [errors, setErrors] = useState({});
  const [tokenizing, setTokenizing] = useState(false);
  // Structured state for the three network-class tokenize failure modes.
  // null when there's no network failure (or the error is a QBO field
  // rejection like card.cvc). Rendered by <PaymentBlockedError> below,
  // which replaces the string-slot friendly copy for these three kinds.
  const [blockError, setBlockError] = useState(null);
  const hasFocusedPayment = useRef(false);
  const fieldRefs = useRef({});

  const setFieldRef = (name) => (el) => {
    if (el) fieldRefs.current[name] = el;
  };

  // Re-warm the submit lambda + QBO token cache when the payment form
  // mounts. QuotePage warms on page load, but a customer can read the
  // quote for longer than an instance stays warm; this second ping right
  // before card entry keeps the actual submit hot. Fire-and-forget.
  useEffect(() => {
    try {
      // Send the whole `<dealId>-<accessToken>` URL segment: the server
      // validates the token before warming, so a guessed deal id cannot
      // trigger an Intuit token refresh.
      const dealParam = (window.location.pathname.split('/q/')[1] || '').split('/')[0];
      fetch(`${process.env.REACT_APP_QUOTE_API_URL || ''}/api/quote/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ warm: true, dealId: dealParam }),
        keepalive: true,
      }).catch(() => {});
    } catch {
      // Ignore: warm-up is purely opportunistic.
    }
  }, []);

  useEffect(() => {
    if (sameAsService && canUseServiceAddress && serviceAddress) {
      const derivedState = serviceAddress.state || stateFromZip(serviceAddress.zip);
      setBillingAddress({
        street: serviceAddress.street || '',
        city: serviceAddress.city || '',
        state: derivedState,
        zip: serviceAddress.zip || '',
      });
    }
  }, [sameAsService, canUseServiceAddress, serviceAddress]);

  const handleMethodChange = (newMethod) => {
    setMethod(newMethod);
    setErrors({});
    setBlockError(null);
    trackEvent(EVENTS.PAYMENT_METHOD_SELECTED, { method: newMethod });
  };

  const handleBillingChange = (e) => {
    const { name, value } = e.target;
    setBillingAddress((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    if (blockError) setBlockError(null);
  };

  const handleCcChange = (e) => {
    const { name, value } = e.target;
    let formatted = value;

    if (name === 'cardNumber') {
      formatted = value.replace(/\D/g, '').slice(0, 16).replace(/(\d{4})(?=\d)/g, '$1 ');
    } else if (name === 'expiry') {
      formatted = value.replace(/\D/g, '').slice(0, 4);
      if (formatted.length > 2) formatted = formatted.slice(0, 2) + '/' + formatted.slice(2);
    } else if (name === 'cvv') {
      formatted = value.replace(/\D/g, '').slice(0, 4);
    }

    setCcFields((prev) => ({ ...prev, [name]: formatted }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    if (blockError) setBlockError(null);
  };

  const handleAchChange = (e) => {
    const { name, value } = e.target;
    let formatted = value;
    if (name === 'routingNumber') formatted = value.replace(/\D/g, '').slice(0, 9);
    if (name === 'accountNumber') formatted = value.replace(/\D/g, '').slice(0, 17);

    setAchFields((prev) => ({ ...prev, [name]: formatted }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: '' }));
    if (blockError) setBlockError(null);
  };

  const ERROR_FIELD_ORDER = [
    'street', 'city', 'state', 'zip',
    'nameOnCard', 'cardNumber', 'expiry', 'cvv',
    'nameOnAccount', 'routingNumber', 'accountNumber',
  ];

  const validate = () => {
    const newErrors = {};

    if (!billingAddress.street.trim()) newErrors.street = 'Required';
    if (!billingAddress.city.trim()) newErrors.city = 'Required';
    // Don't gate on state on our side -- QBO Payments tokenize is the
    // authoritative validator and the existing tokenize-error path
    // surfaces field-specific messages back to the customer. Phone
    // intake sometimes mangles the state ("Caifornia") and our zip-
    // based fallback handles the common case; for anything weirder we
    // want the QBO error rather than a silent client-side block.
    if (!billingAddress.zip.trim()) newErrors.zip = 'Required';

    if (method === 'cc') {
      if (!ccFields.nameOnCard.trim()) newErrors.nameOnCard = 'Required';
      if (ccFields.cardNumber.replace(/\s/g, '').length < 15) newErrors.cardNumber = 'Enter a valid card number';
      if (!/^\d{2}\/\d{2}$/.test(ccFields.expiry)) newErrors.expiry = 'MM/YY';
      if (ccFields.cvv.length < 3) newErrors.cvv = 'Invalid';
    } else {
      if (!achFields.nameOnAccount.trim()) newErrors.nameOnAccount = 'Required';
      if (achFields.routingNumber.length !== 9) newErrors.routingNumber = 'Must be 9 digits';
      if (achFields.accountNumber.length < 4) newErrors.accountNumber = 'Enter a valid account number';
    }

    setErrors(newErrors);

    if (Object.keys(newErrors).length > 0) {
      const firstErrorField = ERROR_FIELD_ORDER.find((f) => newErrors[f]);
      if (firstErrorField && fieldRefs.current[firstErrorField]) {
        fieldRefs.current[firstErrorField].scrollIntoView({ behavior: 'smooth', block: 'center' });
        fieldRefs.current[firstErrorField].focus({ preventScroll: true });
      }
    }

    return Object.keys(newErrors).length === 0;
  };

  const tokenizePayment = async () => {
    let body;
    if (method === 'cc') {
      const [expMonth, expYear] = ccFields.expiry.split('/');
      body = {
        card: {
          name: ccFields.nameOnCard,
          number: ccFields.cardNumber.replace(/\s/g, ''),
          expMonth: expMonth,
          expYear: `20${expYear}`,
          cvc: ccFields.cvv,
          address: {
            streetAddress: billingAddress.street,
            city: billingAddress.city,
            region: billingAddress.state,
            country: 'US',
            postalCode: billingAddress.zip,
          },
        },
      };
    } else {
      const phoneDigits = normalizeUsPhone(customerPhone);
      body = {
        bankAccount: {
          name: achFields.nameOnAccount,
          routingNumber: achFields.routingNumber,
          accountNumber: achFields.accountNumber,
          accountType: achFields.accountType === 'savings' ? 'PERSONAL_SAVINGS' : 'PERSONAL_CHECKING',
          // QBO requires a 10-digit US phone for ACH tokenization. We
          // pass the contact phone from the quote; if for some reason
          // it isn't a valid US number, fall back to the brand phone
          // so we never send the empty string QBO rejected before.
          phone: phoneDigits || normalizeUsPhone(brandConfig?.phone) || '8888174115',
        },
      };
    }

    // One Request-Id reused across attempts. VERIFIED 2026-06-10 against
    // the live Payments endpoint: /payments/tokens does NOT dedupe on
    // Request-Id -- two POSTs with the same id both return 201 with
    // DIFFERENT tokens, never a 4xx. So reuse is harmless but decorative;
    // each attempt mints its own short-lived, single-use token. A retry
    // can at worst orphan an unused token (free, expires ~25min) -- never
    // a double-charge or double-store. (Lucy review 2026-06-10, M#1)
    const requestId = generateRequestId();
    // Fresh 15s AbortSignal per attempt (a timed-out signal can't be
    // reused). Intuit's tokenize endpoint occasionally hangs (latency,
    // network throttling, ad-blocker partial block); the timeout stops a
    // forever-spinner. getData()'s catch maps the timeout to a generic
    // "couldn't process payment" message + reportError.
    const doFetch = () => fetch(QBO_TOKEN_URL, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Request-Id': requestId,
      },
      body: JSON.stringify(body),
      ...(AbortSignal.timeout ? { signal: AbortSignal.timeout(15000) } : {}),
    });

    // Retry policy: mirrors the submit-call retry (QuoteApi.js) and extends
    // it to the Intuit tokenize hop, which previously had none -- Matt
    // Lackey, deal 199942115, 2026-06-10, hit a bare "Failed to fetch" here
    // with no recovery. We retry exactly two transient classes:
    //   (a) a bare network failure: fetch() rejecting with a TypeError
    //       ("Failed to fetch") -- the mobile-blip case. ALLOW-LIST on
    //       name === 'TypeError' (like QuoteApi.js), NOT a deny-list, so we
    //       never retry programming errors.
    //   (b) an Intuit 5xx response -- a transient server blip, consistent
    //       with how the rest of the codebase classifies 5xx as transient
    //       (submit.js, qbo-error-classifier.js).
    // We do NOT retry:
    //   - timeouts: AbortSignal.timeout() rejects with name 'AbortError' in
    //     Chromium and 'TimeoutError' in Firefox/Safari/spec. Both are
    //     DOMExceptions (not TypeErrors), so the allow-list naturally
    //     excludes them -- a genuine timeout falls straight through to the
    //     rethrow. The customer already waited 15s and Intuit may have
    //     received the card (matches submit's "don't retry ECONNABORTED").
    //   - HTTP 4xx: real QBO card-validation rejections a retry can't fix.
    // A 20s total wall-clock deadline caps the worst-case spinner -- the
    // tokenize button is the most abandonment-sensitive step in the funnel,
    // so stacked slow-rejects + backoffs must not run ~45s. (Lucy M#2)
    const BACKOFFS_MS = [1000, 2000]; // up to 2 retries w/ increasing backoff
    const deadlineAt = Date.now() + 20000;
    let response = null;
    // Attempt count is surfaced in tokenize telemetry (QUOTE_TOKENIZE_*
    // events) so we can measure whether the retry is actually recovering
    // customers vs. just adding latency. (Lucy L#2)
    let attemptsUsed = 0;
    for (let attempt = 0; ; attempt += 1) {
      attemptsUsed = attempt + 1;
      let transient = false;
      try {
        response = await doFetch();
        transient = response.status >= 500; // Intuit 5xx blip -> retry (b)
      } catch (err) {
        if (err?.name !== 'TypeError') { err.tokenizeAttempts = attemptsUsed; throw err; } // not a bare network fail
        response = null;
        transient = true; // bare network failure -> retry (a)
      }
      if (!transient) break;
      const outOfTries = attempt >= BACKOFFS_MS.length
        || Date.now() + BACKOFFS_MS[attempt] > deadlineAt;
      if (outOfTries) {
        // 5xx: hand the response to the !response.ok handler below for the
        // normal error copy. Network: rethrow so getData()'s catch fires
        // reportError + the friendly message, same as before this change.
        if (response) break;
        const netErr = new TypeError('Failed to fetch');
        netErr.tokenizeAttempts = attemptsUsed;
        throw netErr;
      }
      await new Promise((resolve) => setTimeout(resolve, BACKOFFS_MS[attempt]));
    }

    if (!response.ok) {
      const err = await response.json().catch(() => ({}));
      const tokErr = new Error(err.errors?.[0]?.message || 'Payment tokenization failed');
      tokErr.tokenizeAttempts = attemptsUsed;
      throw tokErr;
    }

    const data = await response.json();
    return { value: data.value, attempts: attemptsUsed };
  };

  const getData = async () => {
    // Single-flight guard: a mobile customer can tap the parent
    // "Start My Service" button several times during the 20s tokenize
    // window and each tap would previously kick off a fresh submit,
    // launching duplicate Sentry captures, reportError alerts, and
    // block-UI telemetry. Refuse re-entry while a tokenize is already
    // in flight; the parent CTA is separately disabled but the tap
    // race is faster than the state round-trip.
    if (tokenizing) return null;
    // Clear stale block UI at the top of every submit attempt, before
    // validate() so an early return doesn't leave a "payment processor
    // didn't answer" alert stuck on screen while the field-level error
    // for the missing ZIP renders below it.
    setBlockError(null);
    if (!validate()) {
      // Only page ops when prefilled address fields come back empty AND
      // the prefill WAS supposed to populate (canUseServiceAddress).
      // When the service address was incomplete, the billing fields are
      // editable from the start and an empty billing field at submit
      // just means the customer did not type it in: normal user error,
      // not a prefill bug. CC fields empty just means the user hit
      // "Start My Service" before typing them in (Marina Boubion
      // 2026-05-08), which is also normal and should not fire an alert.
      if (canUseServiceAddress && sameAsService) {
        const fieldVals = {
          street: billingAddress.street, city: billingAddress.city,
          state: billingAddress.state, zip: billingAddress.zip,
        };
        const prefilledEmpty = Object.entries(fieldVals)
          .filter(([_, v]) => v == null || !String(v).trim())
          .map(([k]) => k);
        if (prefilledEmpty.length > 0) {
          reportError({
            dealId: routeDealId,
            stage: `validate_${method}_prefill`,
            message: `Prefilled address fields empty after submit: ${prefilledEmpty.join(',')}. sameAsService=${sameAsService}, canUseServiceAddress=${canUseServiceAddress}, serviceAddr=${JSON.stringify(serviceAddress)}, billing=${JSON.stringify(billingAddress)}`,
            contactEmail: customerEmail,
          });
        }
      }
      return null;
    }

    setTokenizing(true);
    setErrors({});
    setBlockError(null);
    const tokenizeStartedAt = Date.now();
    // Fresh correlation id per submit attempt. Threaded through
    // STARTED / COMPLETED / FAILED / BLOCKED / BLOCK_UI_SHOWN /
    // BLOCK_CTA_CLICKED / Sentry / reportError so recovery-funnel
    // analysis can join the events for one customer's one attempt
    // instead of overcounting when a retry re-enters the catch.
    const attemptId = generateRequestId();
    trackEvent(EVENTS.QUOTE_TOKENIZE_STARTED, { method, tokenize_attempt_id: attemptId });

    try {
      const { value: token, attempts } = await tokenizePayment();
      trackEvent(EVENTS.QUOTE_TOKENIZE_COMPLETED, {
        method,
        latency_ms: Date.now() - tokenizeStartedAt,
        attempts,
        tokenize_attempt_id: attemptId,
      });
      return {
        paymentMethod: method,
        billingAddress,
        token,
        // For ACH we surface the last 4 of the account number so ops
        // and the customer's email both show "Bank account ...1234"
        // instead of an empty string.
        cardLast4: method === 'cc'
          ? ccFields.cardNumber.replace(/\s/g, '').slice(-4)
          : achFields.accountNumber.replace(/\D/g, '').slice(-4),
      };
    } catch (err) {
      trackEvent(EVENTS.QUOTE_TOKENIZE_FAILED, {
        method,
        latency_ms: Date.now() - tokenizeStartedAt,
        error: String(err.message || 'unknown').slice(0, 200),
        attempts: err.tokenizeAttempts ?? null,
        tokenize_attempt_id: attemptId,
      });
      // Translate QBO Payments tokenize errors ("bankaccount.routingNumber
      // is invalid", "card.number is invalid", etc.) into human-readable
      // copy AND map them back to the form field so the user gets
      // scrolled to the offending input. The QBO message is also stashed
      // in `_tokenize` for ops debugging via the alert ticket.
      const fieldMap = {
        'card.number': { field: 'cardNumber', msg: "We couldn't read that card number. Double-check the digits and try again." },
        'card.cvc': { field: 'cvv', msg: "That security code (CVV) doesn't match the card. It's the 3-digit code on the back (or 4 digits on the front for Amex)." },
        'card.expmonth': { field: 'expiry', msg: "That expiration date doesn't look right. Use MM/YY format." },
        'card.expyear': { field: 'expiry', msg: "That expiration date doesn't look right. Use MM/YY format." },
        'card.name': { field: 'nameOnCard', msg: "Please enter the name exactly as it appears on the card." },
        'card.address.streetaddress': { field: 'street', msg: "That billing street address doesn't match the card. Try the address on your most recent statement." },
        'card.address.city': { field: 'city', msg: "That billing city doesn't match the card. Try the address on your most recent statement." },
        'card.address.region': { field: 'state', msg: "That billing state doesn't match the card. Try the address on your most recent statement." },
        'card.address.postalcode': { field: 'zip', msg: "That billing zip code doesn't match the card. Try the zip from your most recent statement." },
        'bankaccount.routingnumber': { field: 'routingNumber', msg: "That routing number doesn't look like a real US bank routing number. Double-check the 9 digits." },
        'bankaccount.accountnumber': { field: 'accountNumber', msg: "That account number isn't accepted. Double-check it against your bank statement." },
        'bankaccount.name': { field: 'nameOnAccount', msg: "Please enter the name exactly as it appears on the bank account." },
        'bankaccount.phone': { field: 'nameOnAccount', msg: "We couldn't validate the bank account. Refresh the page and try again -- if it keeps failing, give us a call." },
      };
      const m = String(err.message || '').toLowerCase().match(/([a-z]+\.[a-z.]+)/);
      const mapped = m ? fieldMap[m[1]] : null;
      const inferredField = mapped?.field || null;

      // Network-class failure (the browser->Intuit fetch rejected) vs a
      // QBO field-validation error. This is the Matt Lackey case: a
      // device-side ad blocker / VPN / private DNS eating the call to
      // api.intuit.com. It was invisible to ops (caught here, never sent
      // to Sentry), so on this branch we: (a) run a connectivity probe to
      // fingerprint WHAT is blocked, (b) captureException to Sentry so we
      // get breadcrumbs + a session replay of the failing session, and
      // (c) tell the customer the real cause so they can self-heal. All of
      // this runs only on a failure that already happened -> no happy-path
      // latency.
      const isNetworkErr = !inferredField && (
        err?.name === 'TypeError'
        || /failed to fetch|load failed|networkerror|network request failed/i.test(String(err.message || ''))
      );
      // blockKind classifies the network failure so <PaymentBlockedError>
      // can render the right structured UI. 'transient' is the "everything
      // reachable by probe time" bucket first seen live with Bruce Steans
      // (deal 200002372, 2026-06-12): a wifi-to-cellular handoff that
      // outlived the 3-attempt retry window while a server-side tokenize
      // POST returned 201. Falling through to "double-check the details"
      // sent customers re-typing a card that was never the problem.
      let blockKind = null;
      let probe = null;
      let probeClass = null;
      if (isNetworkErr) {
        probe = await probeConnectivity();
        // block_kind is the product-facing narrative (three copy
        // buckets). probe_class is the observability-facing raw
        // classification of the {intuit, origin, control} triple so
        // Sentry / Statsig searches can distinguish an inconclusive
        // captive-portal 'mixed' from a wifi-to-cellular 'all_reachable'
        // without overloading block_kind.
        if (!probe.intuit && probe.origin && probe.control) {
          blockKind = 'device-block'; probeClass = 'intuit_only_unreachable';
        } else if (!probe.origin && !probe.control) {
          blockKind = 'offline'; probeClass = 'offline';
        } else if (probe.intuit && probe.origin && probe.control) {
          blockKind = 'transient'; probeClass = 'all_reachable';
        } else {
          blockKind = 'transient'; probeClass = 'mixed';
        }
        try {
          Sentry.captureException(err, {
            tags: {
              stage: `tokenize_${method}`,
              failure_kind: 'network',
              block_kind: blockKind,
              probe_class: probeClass,
              intuit_reachable: String(probe.intuit),
              origin_reachable: String(probe.origin),
              control_reachable: String(probe.control),
              tokenize_attempt_id: attemptId,
            },
            extra: {
              dealId: routeDealId,
              attempts: err.tokenizeAttempts ?? null,
              navigatorOnLine: typeof navigator !== 'undefined' ? navigator.onLine : null,
              connection: (typeof navigator !== 'undefined' && navigator.connection)
                ? {
                    effectiveType: navigator.connection.effectiveType,
                    downlink: navigator.connection.downlink,
                    rtt: navigator.connection.rtt,
                    saveData: navigator.connection.saveData,
                  }
                : null,
              probe,
            },
          });
        } catch { /* Sentry is best-effort; never block the customer on it */ }
        trackEvent(EVENTS.QUOTE_TOKENIZE_BLOCKED, {
          method,
          block_kind: blockKind,
          probe_class: probeClass,
          intuit_reachable: probe.intuit,
          origin_reachable: probe.origin,
          control_reachable: probe.control,
          tokenize_attempt_id: attemptId,
        });
      }

      // Field-level QBO validation errors (card.cvc, bankaccount.routing,
      // etc.) still flow through the string-slot errors._tokenize because
      // the message wants to scroll to the offending input. Network-class
      // failures switch to the structured PaymentBlockedError component,
      // which owns its own CTA layer and its own scrollIntoView.
      // BLOCK_UI_SHOWN fires from the component's own effect (onShown),
      // not from here, so it stays keyed to the actual render instance
      // instead of every catch-path entry.
      if (blockKind) {
        const browser = detectBlockingBrowser();
        setBlockError({
          kind: blockKind,
          method,
          browser,
          attemptId,
          probeClass,
        });
        setErrors({});
      } else {
        const friendlyMsg = mapped?.msg || `We couldn't process that payment method. Double-check the details and try again.`;
        const fieldErrors = inferredField
          ? { [inferredField]: friendlyMsg, _tokenize: friendlyMsg }
          : { _tokenize: friendlyMsg };
        setErrors(fieldErrors);
      }

      if (inferredField && fieldRefs.current[inferredField]) {
        fieldRefs.current[inferredField].scrollIntoView({ behavior: 'smooth', block: 'center' });
        fieldRefs.current[inferredField].focus({ preventScroll: true });
      }

      trackEvent(EVENTS.QUOTE_SUBMIT_ERROR, {
        error: `tokenize_${err.message}`,
        tokenize_attempt_id: attemptId,
      });
      // Tokenize failures hit a third-party endpoint (Intuit) and
      // never reach our backend, so we report them client-side here
      // to make sure ops still gets notified. Pure form-validation
      // errors short-circuit earlier in validate(); only real failures
      // reach this catch.
      reportError({
        dealId: routeDealId,
        stage: `tokenize_${method}`,
        message: `${err.message || 'unknown tokenize error'}`
          + `${probe ? ` | reachable: intuit=${probe.intuit} origin=${probe.origin} control=${probe.control}` : ''}`
          + `${err.tokenizeAttempts != null ? ` | attempts=${err.tokenizeAttempts}` : ''}`
          + `${blockKind ? ` | block_kind=${blockKind}` : ''}`
          + `${probeClass ? ` | probe_class=${probeClass}` : ''}`
          + ` | attempt_id=${attemptId}`,
        contactEmail: customerEmail,
      });
      return null;
    } finally {
      setTokenizing(false);
    }
  };

  const handlePaymentFocus = () => {
    if (!hasFocusedPayment.current) {
      hasFocusedPayment.current = true;
      trackEvent(EVENTS.PAYMENT_FIELD_FOCUSED, { method });
    }
  };

  useImperativeHandle(ref, () => ({ getData, isTokenizing: () => tokenizing }));

  // Mobile-abandon signal: report once per field when the customer
  // touched a payment input but moved on with it still empty. We only
  // fire once per field to avoid noise; the funnel cares whether a
  // field was ever-blurred-empty, not how many times it bounced.
  const blurredEmptyFieldsRef = useRef(new Set());
  const handleFieldBlurForEmpty = (fieldName) => (e) => {
    if (blurredEmptyFieldsRef.current.has(fieldName)) return;
    if (e.target && !String(e.target.value || '').trim()) {
      blurredEmptyFieldsRef.current.add(fieldName);
      trackEvent(EVENTS.QUOTE_PAYMENT_FIELD_BLURRED_EMPTY, {
        field: fieldName,
        method,
      });
    }
  };

  const showBillingFields = !sameAsService || !canUseServiceAddress;

  // The 3.5% card processing fee applies to ALL card payments (monthly
  // autopay and one-time card-at-service charges); ACH carries no fee.
  // Disclosing it here, at the decision point, is deliberate: the fee
  // otherwise first appears on the customer's bill, which reads as a
  // hidden fee and drives churn/chargebacks. Concrete dollar framing
  // ("$6.65/mo") reads smaller than an abstract percentage and the
  // zero-fee ACH path sits one tap away as the savings alternative.
  const ccFeePct = `${(CC_FEE_RATE * 100).toFixed(1).replace(/\.0$/, '')}%`;
  const fmtFee = (n) => `$${n.toFixed(2)}`;
  const ccFeeDetail = (() => {
    const monthly = Number(monthlyPrice);
    const oneTime = Number(oneTimeTotal);
    const feeOf = (n) => fmtFee(Math.round(n * CC_FEE_RATE * 100) / 100);
    const hasMonthlyFee = isMonthly && Number.isFinite(monthly) && monthly > 0;
    const hasOneTimeFee = Number.isFinite(oneTime) && oneTime > 0;
    // Mixed quotes (monthly plan plus quoted one-time work) carry the
    // fee on both charges; show both so the concrete number is never
    // an understatement. amountLabel stays the short recurring figure
    // for the compact bank-switch link.
    if (hasMonthlyFee && hasOneTimeFee) {
      const mo = `${feeOf(monthly)}/mo`;
      const both = `${mo} on your plan plus ${feeOf(oneTime)} on the one-time work`;
      return { amountLabel: mo, noteDetail: both, savingsLabel: both };
    }
    if (hasMonthlyFee) {
      const mo = `${feeOf(monthly)}/mo`;
      return { amountLabel: mo, noteDetail: `${mo} on your plan`, savingsLabel: mo };
    }
    if (!isMonthly && hasOneTimeFee) {
      const ot = feeOf(oneTime);
      return { amountLabel: ot, noteDetail: `${ot} on this proposal`, savingsLabel: ot };
    }
    return null;
  })();

  return (
    <div className="qp-payment-section" onFocus={handlePaymentFocus}>
      <h3 className="qp-section-title">Payment Information</h3>

      <p className="qp-no-charge-banner">
        <IoLockClosed aria-hidden="true" className="qp-no-charge-lock" />
        <span>
          <strong>No charge today.</strong>{' '}
          {isMonthly
            ? "We keep your payment method on file for autopay; the first charge is after your first scheduled visit."
            : "We charge your payment method only after the visit is complete."}
        </span>
      </p>

      {achRevealed && (
        <div className="qp-payment-toggle" role="radiogroup" aria-label="Payment method">
          <button
            type="button"
            role="radio"
            aria-checked={method === 'cc'}
            className={`qp-toggle-btn ${method === 'cc' ? 'active' : ''}`}
            onClick={() => handleMethodChange('cc')}
          >
            Credit Card
            {method === 'cc' && <span className="qp-popular-badge">{ccFeePct} processing fee</span>}
          </button>
          <button
            type="button"
            role="radio"
            aria-checked={method === 'ach'}
            className={`qp-toggle-btn ${method === 'ach' ? 'active' : ''}`}
            onClick={() => handleMethodChange('ach')}
          >
            Bank Account (ACH)
            {method === 'ach' && <span className="qp-popular-badge">No card fees</span>}
          </button>
        </div>
      )}

      {/* Billing address. Hidden behind "same as service" checkbox by
          default so the customer doesn't have to retype an address we
          already have. ZIP lives inside the billing block (no inline
          card-block ZIP) -- when the service ZIP matches the card's
          billing ZIP we just use that; when it doesn't, the customer
          unchecks "same as service" to expose the full address and
          override the ZIP for AVS. */}
      <div className="qp-field-group">
        {canUseServiceAddress && (
          <label className="qp-checkbox-row">
            <input
              type="checkbox"
              checked={sameAsService}
              onChange={(e) => setSameAsService(e.target.checked)}
            />
            <span>Billing address same as service address</span>
          </label>
        )}

        {showBillingFields && (
          <>
            {!canUseServiceAddress && (
              <div className="qp-error" style={{ background: '#fff3cd', border: '1px solid #f0c674', color: '#7c5e00', padding: '10px 12px', borderRadius: 6, marginTop: 8 }}>
                Please enter your billing address below.
              </div>
            )}
            <label className="qp-field-label">Billing Address</label>
            <input
              ref={setFieldRef('street')}
              className={`qp-input ${errors.street ? 'qp-input-error' : ''}`}
              name="street"
              placeholder="Street address"
              autoComplete="street-address"
              value={billingAddress.street}
              onChange={handleBillingChange}
            />
            {errors.street && <span className="qp-error">{errors.street}</span>}
            <div className="qp-input-row qp-access-row-stack">
              <div className="qp-input-col">
                <input
                  ref={setFieldRef('city')}
                  className={`qp-input ${errors.city ? 'qp-input-error' : ''}`}
                  name="city"
                  placeholder="City"
                  autoComplete="address-level2"
                  value={billingAddress.city}
                  onChange={handleBillingChange}
                />
                {errors.city && <span className="qp-error">{errors.city}</span>}
              </div>
              <div className="qp-input-col qp-input-col-sm">
                <input
                  ref={setFieldRef('state')}
                  className={`qp-input ${errors.state ? 'qp-input-error' : ''}`}
                  name="state"
                  placeholder="State"
                  autoComplete="address-level1"
                  maxLength={2}
                  value={billingAddress.state}
                  onChange={handleBillingChange}
                />
                {errors.state && <span className="qp-error">{errors.state}</span>}
              </div>
              <div className="qp-input-col qp-input-col-sm">
                <input
                  ref={setFieldRef('zip')}
                  className={`qp-input ${errors.zip ? 'qp-input-error' : ''}`}
                  name="zip"
                  placeholder="ZIP"
                  inputMode="numeric"
                  autoComplete="postal-code"
                  maxLength={5}
                  value={billingAddress.zip}
                  onChange={handleBillingChange}
                />
                {errors.zip && <span className="qp-error">{errors.zip}</span>}
              </div>
            </div>
          </>
        )}
      </div>

      {/* Credit Card Fields. ZIP for AVS lives in the billing-address
          section above (not inline here). When sameAsService is
          checked the service ZIP gets used for AVS; when it differs
          the customer unchecks "same as service" and overrides the
          billing ZIP there. Putting ZIP inline next to CVC is the
          Stripe/Square/Braintree pattern but iOS Safari + iCloud
          Keychain don't reliably cross-fill into it, so we put ZIP
          where it semantically belongs (the billing block). */}
      {method === 'cc' && (
        <div className="qp-field-group">
          <label className="qp-field-label">Card Details</label>
          <input
            ref={setFieldRef('nameOnCard')}
            className={`qp-input ${errors.nameOnCard ? 'qp-input-error' : ''}`}
            name="nameOnCard"
            placeholder="Name on card"
            autoComplete="cc-name"
            value={ccFields.nameOnCard}
            onChange={handleCcChange}
          />
          {errors.nameOnCard && <span className="qp-error">{errors.nameOnCard}</span>}
          <input
            ref={setFieldRef('cardNumber')}
            className={`qp-input ${errors.cardNumber ? 'qp-input-error' : ''}`}
            name="cardNumber"
            placeholder="Card number"
            inputMode="numeric"
            autoComplete="cc-number"
            value={ccFields.cardNumber}
            onChange={handleCcChange}
            onBlur={handleFieldBlurForEmpty('cardNumber')}
          />
          {errors.cardNumber && <span className="qp-error">{errors.cardNumber}</span>}
          <div className="qp-input-row">
            <div className="qp-input-col">
              <input
                ref={setFieldRef('expiry')}
                className={`qp-input ${errors.expiry ? 'qp-input-error' : ''}`}
                name="expiry"
                placeholder="MM/YY"
                inputMode="numeric"
                autoComplete="cc-exp"
                value={ccFields.expiry}
                onChange={handleCcChange}
                onBlur={handleFieldBlurForEmpty('expiry')}
              />
              {errors.expiry && <span className="qp-error">{errors.expiry}</span>}
            </div>
            <div className="qp-input-col qp-input-col-sm">
              <input
                ref={setFieldRef('cvv')}
                className={`qp-input ${errors.cvv ? 'qp-input-error' : ''}`}
                name="cvv"
                placeholder="CVV"
                inputMode="numeric"
                autoComplete="cc-csc"
                value={ccFields.cvv}
                onChange={handleCcChange}
                onBlur={handleFieldBlurForEmpty('cvv')}
              />
              {errors.cvv && <span className="qp-error">{errors.cvv}</span>}
            </div>
          </div>
          <p className="qp-cc-fee-note">
            A {ccFeePct} card processing fee
            {ccFeeDetail ? ` (${ccFeeDetail.noteDetail})` : ''} is
            added to card payments. Bank account (ACH) payments have no fee.
          </p>
          {!achRevealed && (
            <button
              type="button"
              className="qp-pay-with-bank-link"
              onClick={() => {
                setAchRevealed(true);
                trackEvent(EVENTS.QUOTE_ACH_REVEALED, {});
                handleMethodChange('ach');
              }}
            >
              Pay with bank account instead
              {ccFeeDetail ? ` (save ${ccFeeDetail.amountLabel})` : ' (no card fees)'}
            </button>
          )}
        </div>
      )}

      {/* ACH Fields */}
      {method === 'ach' && (
        <div className="qp-field-group">
          <p className="qp-ach-no-fee-note">
            No processing fees with a bank account
            {ccFeeDetail ? `: you save ${ccFeeDetail.savingsLabel} vs. paying by card` : ''}.
          </p>
          <label className="qp-field-label">Bank Account Details</label>
          <input
            ref={setFieldRef('nameOnAccount')}
            className={`qp-input ${errors.nameOnAccount ? 'qp-input-error' : ''}`}
            name="nameOnAccount"
            placeholder="Name on account"
            value={achFields.nameOnAccount}
            onChange={handleAchChange}
          />
          {errors.nameOnAccount && <span className="qp-error">{errors.nameOnAccount}</span>}
          <div className="qp-input-row">
            <div className="qp-input-col">
              <input
                ref={setFieldRef('routingNumber')}
                className={`qp-input ${errors.routingNumber ? 'qp-input-error' : ''}`}
                name="routingNumber"
                placeholder="Routing number"
                inputMode="numeric"
                value={achFields.routingNumber}
                onChange={handleAchChange}
                onBlur={handleFieldBlurForEmpty('routingNumber')}
              />
              {errors.routingNumber && <span className="qp-error">{errors.routingNumber}</span>}
            </div>
            <div className="qp-input-col">
              <input
                ref={setFieldRef('accountNumber')}
                className={`qp-input ${errors.accountNumber ? 'qp-input-error' : ''}`}
                name="accountNumber"
                placeholder="Account number"
                inputMode="numeric"
                value={achFields.accountNumber}
                onChange={handleAchChange}
                onBlur={handleFieldBlurForEmpty('accountNumber')}
              />
              {errors.accountNumber && <span className="qp-error">{errors.accountNumber}</span>}
            </div>
          </div>
          <div className="qp-account-type">
            <label>
              <input
                type="radio"
                name="accountType"
                value="checking"
                checked={achFields.accountType === 'checking'}
                onChange={handleAchChange}
              />
              Checking
            </label>
            <label>
              <input
                type="radio"
                name="accountType"
                value="savings"
                checked={achFields.accountType === 'savings'}
                onChange={handleAchChange}
              />
              Savings
            </label>
          </div>
        </div>
      )}

      {blockError && (
        <PaymentBlockedError
          kind={blockError.kind}
          method={blockError.method}
          browser={blockError.browser}
          phone={brandConfig?.phone || '(888) 817-4115'}
          onShown={() => trackEvent(EVENTS.QUOTE_TOKENIZE_BLOCK_UI_SHOWN, {
            method: blockError.method,
            block_kind: blockError.kind,
            probe_class: blockError.probeClass,
            browser_id: blockError.browser?.id,
            tokenize_attempt_id: blockError.attemptId,
          })}
          onCallClick={() => trackEvent(EVENTS.QUOTE_TOKENIZE_BLOCK_CTA_CLICKED, {
            method: blockError.method,
            block_kind: blockError.kind,
            probe_class: blockError.probeClass,
            browser_id: blockError.browser?.id,
            tokenize_attempt_id: blockError.attemptId,
            cta: 'call',
          })}
          onSmsClick={() => trackEvent(EVENTS.QUOTE_TOKENIZE_BLOCK_CTA_CLICKED, {
            method: blockError.method,
            block_kind: blockError.kind,
            probe_class: blockError.probeClass,
            browser_id: blockError.browser?.id,
            tokenize_attempt_id: blockError.attemptId,
            cta: 'sms',
          })}
          onRetryClick={() => {
            trackEvent(EVENTS.QUOTE_TOKENIZE_BLOCK_CTA_CLICKED, {
              method: blockError.method,
              block_kind: blockError.kind,
              probe_class: blockError.probeClass,
              browser_id: blockError.browser?.id,
              tokenize_attempt_id: blockError.attemptId,
              cta: 'retry',
            });
            setBlockError(null);
          }}
        />
      )}

      {errors._tokenize && !blockError && (
        <div className="qp-submit-error" role="alert" style={{ marginBottom: 12 }}>
          {errors._tokenize}
        </div>
      )}

      {tokenizing && (
        <div className="qp-tokenizing" aria-live="polite">
          <div className="qp-loading-spinner" style={{ width: 20, height: 20, borderWidth: 2 }} />
          <span>Securely processing your payment info...</span>
        </div>
      )}

      <div className="qp-security-badge">
        <IoLockClosed size={14} aria-hidden="true" />
        <span>Your payment details go directly to Intuit (QuickBooks). We never see or store your card number.</span>
      </div>
    </div>
  );
});

export default PaymentForm;
